import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { StatusPill } from "@/components/parcelio/status-pill";
import { LiveMap } from "@/components/parcelio/live-map";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { parcelsApi, directoryApi, type Parcel, type DriverDirectoryEntry, type ParcelLocation } from "@/lib/api";
import { parcelProgress, parcelStatusLabel, toLegacyStatus } from "@/lib/parcel-adapters";
import { Search, Filter, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";


export default function TrackingList() {
  const token = useAuth((s) => s.session?.accessToken);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | Parcel["status"]>("all");
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [activeLocation, setActiveLocation] = useState<ParcelLocation>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, d] = await Promise.all([parcelsApi.list(undefined, token), directoryApi.drivers(token)]);
        if (cancelled) return;
        setParcels(p);
        setDrivers(d);
      } catch {
        toast.error("Failed to load parcels");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = parcels.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      p.tracking_id.toLowerCase().includes(s) ||
      (p.receiver_name ?? "").toLowerCase().includes(s) ||
      (p.dropoff_addr ?? "").toLowerCase().includes(s)
    );
  });

  const active = filtered[0];

  useEffect(() => {
    let cancelled = false;
    if (!active) {
      setActiveLocation(null);
      return;
    }
    (async () => {
      try {
        const loc = await parcelsApi.location(active.id);
        if (!cancelled) setActiveLocation(loc);
      } catch {
        if (!cancelled) setActiveLocation(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active?.id]);

  return (
    <>
      <Topbar title="Live tracking" subtitle={`${filtered.length} parcels in view`} />
      <div className="flex-1 p-4 md:p-6">
        <div className="grid gap-4 lg:grid-cols-[380px,1fr]">
          {/* List */}
          <div className="surface-card flex flex-col overflow-hidden">
            <div className="space-y-3 border-b p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Tracking #, recipient, address…"
                  className="pl-9"
                  aria-label="Search parcels"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["all", "All"],
                    ["assigned", "Assigned"],
                    ["in_transit", "In transit"],
                    ["failed", "Failed"],
                    ["delivered", "Delivered"],
                  ] as const
                ).map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setFilter(v)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      filter === v ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <ul className="max-h-[70vh] divide-y overflow-y-auto">
              {loading ? <LoadingBlock label="Loading parcels…" /> : null}
              {!loading && filtered.map((p) => {
                const d = drivers.find((x) => x.id === p.driver_id);
                return (
                  <li key={p.id}>
                    <Link
                      to={`/app/tracking/${p.id}`}
                      className="block px-4 py-3 hover:bg-muted/50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-semibold text-muted-foreground">{p.tracking_id}</span>
                        <StatusPill status={toLegacyStatus(p.status)} />
                      </div>
                      <div className="mt-1 truncate text-sm font-medium">{p.receiver_name ?? "—"}</div>
                      <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" /> {p.dropoff_addr ?? "—"}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{d?.name ?? "Unassigned"}</span>
                        <span className="font-medium">{parcelStatusLabel(p.status)}</span>
                      </div>
                      <Progress value={parcelProgress(p.status)} className="mt-1 h-1" />
                    </Link>
                  </li>
                );
              })}
              {!loading && filtered.length === 0 ? (
                <li className="p-6 text-center text-sm text-muted-foreground">No parcels match your filters.</li>
              ) : null}
            </ul>
          </div>

          {/* Map preview */}
          <div className="surface-card overflow-hidden">
            {loading ? (
              <LoadingBlock label="Loading tracking…" className="min-h-96" />
            ) : active ? (
              <>
                <div className="flex items-center justify-between border-b p-4">
                  <div>
                    <div className="font-mono text-xs font-semibold text-muted-foreground">{active.tracking_id}</div>
                    <div className="text-lg font-semibold">{active.receiver_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{active.dropoff_addr ?? "—"}</div>
                  </div>
                  <Button asChild>
                    <Link to={`/app/tracking/${active.id}`}>
                      Open details
                    </Link>
                  </Button>
                </div>
                <LiveMap
                  current={activeLocation ? { lat: activeLocation.lat, lng: activeLocation.lng, label: "Live position" } : null}
                  className="aspect-[16/10] w-full"
                />
                <div className="grid grid-cols-2 gap-4 p-4">
                  <StatBlock label="Status" value={parcelStatusLabel(active.status)} />
                  <StatBlock label="Progress (est.)" value={`${parcelProgress(active.status)}%`} />
                </div>
              </>
            ) : (
              <div className="flex h-full min-h-96 flex-col items-center justify-center gap-2 p-8 text-center">
                <Filter className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">Select a parcel to preview live tracking.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-semibold">{value}</div>
    </div>
  );
}
